"use client";

import NextLink, { type LinkProps } from "next/link";
import { Icon, Link, Text } from "@stone-payments/jade";
import { Fragment } from "react";
import { Skeleton } from "@/components/skeleton";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

type Props = {
  items: BreadcrumbItem[];
  loading?: boolean;
  onLinkClick?: LinkProps["onClick"];
};

type ListProps = Pick<Props, "items" | "onLinkClick">;

const Divider = () => (
  <li className="flex items-center">
    <Icon use="chevron-right" size="small" color="low" />
  </li>
);

const Loading = () => (
  <>
    <li className="flex items-center">
      <Skeleton className="rounded-sm" height="h-[20px]" width="w-[36px]" />
    </li>
    <Divider />
    <li className="flex items-center">
      <Skeleton className="rounded-sm" height="h-[20px]" width="w-[73px]" />
    </li>
    <Divider />
    <li className="flex items-center">
      <Skeleton className="rounded-sm" height="h-[20px]" width="w-[73px]" />
    </li>
  </>
);

const List = ({ items, onLinkClick }: ListProps) => {
  return items.map((item, index) => (
    <Fragment key={item.href ?? index}>
      <li className="flex items-center">
        {item.href ? (
          <NextLink href={item.href} passHref>
            <Link
              trailing="none"
              size="medium"
              as="span"
              className="wrap-break-word"
              onClick={onLinkClick}
            >
              {item.label}
            </Link>
          </NextLink>
        ) : (
          <Text
            color="low"
            weight="medium"
            className="wrap-break-word"
            aria-current="page"
          >
            {item.label}
          </Text>
        )}
      </li>
      {index < items.length - 1 && <Divider />}
    </Fragment>
  ));
};

export const Breadcrumbs = ({
  items,
  loading = false,
  onLinkClick,
  ...rest
}: Props) => {
  if (items?.length === 0) return null;

  return (
    <nav className="mx-5" {...rest}>
      <ol
        className="flex flex-wrap items-center space-x-2"
        aria-label="Caminho detalhado"
      >
        {loading ? (
          <Loading />
        ) : (
          <List items={items} onLinkClick={onLinkClick} />
        )}
      </ol>
    </nav>
  );
};
